<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from "pinia";
import OverlaySending from '../organisms/overlay/OverlaySending.vue';
import LayoutDefault from '../layouts/LayoutDefault.vue';
import { useGdprTextsStore } from '../../stores/gdpr-texts-store';
import { saveApplication } from '../../services/applicant';
import { EVENTS, FUNNEL_STEP, FUNNEL_TYPES, FUNNEL_VARIANTS, track } from '../../utils/tracking';
import config from '../../config';
import { PAGE_FORM, PAGE_SUCCESS } from '../../routes';
import * as logger from '../../utils/logger';
import { useApplicationStore } from '../../stores/application-store';
import { usePayloadStore } from '../../stores/payload-store';
import { useProfile } from '../../stores/profile-store';
import { useJobDataStore } from '../../stores/job-store';
import { log } from '../../utils/actionLogger';
import { useWebViewContext } from '../../composables/webview-context';
import { useAuth } from '../../stores/auth-store';
import { isFeatureActive } from '../../composables/feature-flags';

const root = ref(null);
const router = useRouter();
const auth = useAuth();

/* Web view */
const { isWebView, deeplinkToAppRoute } = useWebViewContext();

function fetchRecaptchaToken() {
    return new Promise((resolve, reject) => {
        if (config.googleRecaptcha.active) {
            try {
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(config.googleRecaptcha.siteKey)
                        .then((token) => {
                            resolve(token);
                        });
                });
            } catch (e) {
                reject();
            }
        } else {
            // Needed for staging and local environment, if recaptcha is turned off.
            // There are problems with acceptance tests, when this value is boolean, because this
            // value is then set as recaptcha response into the store and later on set as request header.
            resolve('true');
        }
    });
}

const application = useApplicationStore();
const payload = usePayloadStore();
const profile = useProfile();
const jobData = useJobDataStore();
const {
    email, extractedSuccess, extractedEmail, firstname, extractedFirstname, lastname, extractedLastname,
    phoneNumber, extractedPhoneNumber,
} = storeToRefs(application);

const documentsTrackingElementDetail = computed(() => {
    if (application.anyProfileDocumentIsInActiveAttachments && application.anyUploadedDocumentIsInActiveAttachments) {
        return 'profile,custom';
    } if (application.anyProfileDocumentIsInActiveAttachments) {
        return 'profile';
    } if (application.anyUploadedDocumentIsInActiveAttachments) {
        return 'custom';
    }
    return 'none';
});

const cvTrackingElementDetail = computed(() => {
    if (auth.isLoggedIn) {
        return profile.hasProfile ? 'with profile' : 'logged-in';
    }

    return 'logged-out';
});

const differencesBetweenCvExtractionAndSubmitted = computed(() => {
    if (!extractedSuccess.value) return [];

    const fields = [
        { field: 'email', extracted: extractedEmail, current: email },
        { field: 'firstname', extracted: extractedFirstname, current: firstname },
        { field: 'surname', extracted: extractedLastname, current: lastname },
        { field: 'phoneNumber', extracted: extractedPhoneNumber, current: phoneNumber },
    ];

    return fields
        .filter(({ extracted }) => extracted.value)
        .filter(({ extracted, current }) => extracted.value !== current.value)
        .map(({ field, extracted, current }) => ({
            field,
            original: extracted.value,
            changed: current.value,
        }));
});

async function apply() {
    let token;
    try {
        token = await fetchRecaptchaToken();
    } catch (exception) {
        await router.replace({ name: PAGE_FORM });
        logger.error({
            exception,
            fingerprint: ['Fetching recaptcha failed.'],
            message: 'Fetching recaptcha failed.',
        });
        return;
    }

    if (!token) return;
    application.recaptchaResponse = token;

    const gdprTexts = useGdprTextsStore();

    log('on-application-form-successful-frontend-validation');

    const state = {
        application,
        gdprTexts: gdprTexts.$state,
        payload: payload.$state,
        profile: profile.state,
        customFields: jobData.serializedCustomFields,
        meta: {
            clientApplicationUuid: application.applicationUuid,
        },
    };

    if (differencesBetweenCvExtractionAndSubmitted.value.length) {
        state.clientApplicationUuid = application.applicationUuid;
        state.meta.cvExtraction = differencesBetweenCvExtractionAndSubmitted.value;
    }

    try {
        await saveApplication(state);
    } catch (e) {
        await router.replace({
            name: PAGE_FORM,
        });
        return;
    }

    track({
        element: 'AP_F: Submit - Success',
        elementDetail: 'Ad Click - smart bewerben button',
    });

    // CV selection tracking
    track({
        element: `AP_F: CV: ${application.selectedCvType}`,
        elementDetail: `User: ${cvTrackingElementDetail.value}`,
    });

    // Other documents tracking
    track({
        element: `AP_F: Documents: ${documentsTrackingElementDetail.value}`,
        elementDetail: `User: ${cvTrackingElementDetail.value}`,
    });

    if (application.showApplicationLetter) {
        track({
            element: 'AP_F: Submit - Success',
            elementDetail: 'Remarks',
        });
    }

    track({
        event: EVENTS.funnel,
        funnelType: FUNNEL_TYPES.application,
        funnelStep: FUNNEL_STEP.success,
        funnelVariant: jobData.hasCustomFields ? FUNNEL_VARIANTS.customFields : FUNNEL_VARIANTS.default,
    });

    track({
        event: EVENTS.jobApplySuccess,
        jobId: jobData.jobId,
        jobType: jobData.isSeamless ? 'SJA' : 'Standard',
        productType: jobData.productType,
        applicationType: 'SmartBewerben',
        userData: {
            email: profile.email ?? application.email ?? null,
        },
    });

    track({
        element: 'job_apply_success_ab_test',
        elementDetail: isFeatureActive('required-login') ?
            'Login Wall' :
            'Control',
    });

    track({
        element: 'AP_F: Application Submitted - CV Extraction',
        elementDetail: `${application.cvExtractionUsed ? 'Yes' : 'No'}`,
    });

    if (application.hasSeenPreview) {
        track({
            element: 'AP_F: Submit - Success',
            elementDetail: 'Seen preview',
        });
    }

    if (application.isMailDifferentFromAccount) {
        track({
            element: 'AP_F: Email different from profile',
        });
    }

    if (application.isFirstnameDifferentFromAccount) {
        track({
            element: 'AP_F: Firstname different from profile',
        });
    }

    if (application.isLastnameDifferentFromAccount) {
        track({
            element: 'AP_F: Surname different from profile',
        });
    }

    if (application.isSalutationDifferentFromAccount) {
        track({
            element: 'AP_F: Salutation different from profile',
        });
    }

    if (application.isPhoneNumberDifferentFromAccount) {
        track({
            element: 'AP_F: Phone number different from profile',
        });
    }

    if (isWebView.value) {
        deeplinkToAppRoute('application-success');
        return;
    }

    await router.replace({
        name: PAGE_SUCCESS,
    });
}

onMounted(apply);
</script>

<template>
    <LayoutDefault ref="root">
        <template #main>
            <OverlaySending/>
        </template>
    </LayoutDefault>
</template>
